<template>
  <div class="supply-riepilogo">
    <div class="riepilogo-header">
      <div class="riepilogo-header-title">
        <i class="pi pi-info-circle"></i>
        <span>Riepilogo</span>
      </div>
      <div class="riepilogo-header-actions">
        <slot name="header-actions" />
        <button
          type="button"
          class="riepilogo-incall-btn"
          :disabled="!canOpenIncall"
          title="Apri in In Call"
          @click="openInCall"
        >
          <i class="pi pi-phone"></i>
          In Call
        </button>
      </div>
    </div>

    <div class="riepilogo-grid">
      <div class="riepilogo-card">
        <div class="riepilogo-label">
          <i :class="entityType === 'rappresentante' ? 'pi pi-users' : 'pi pi-user'"></i>
          {{ entityLabel }}
        </div>
        <div class="riepilogo-value">
          <router-link
            v-if="resolvedClienteId"
            :to="entityDetailPath"
            class="riepilogo-link"
          >
            {{ resolvedClienteNome || `${entityLabel} #${resolvedClienteId}` }}
          </router-link>
          <span v-else class="riepilogo-muted">Non disponibile</span>
        </div>
      </div>

      <div class="riepilogo-card" :class="hasDebito ? 'riepilogo-card--warning' : 'riepilogo-card--ok'">
        <div class="riepilogo-label">
          <i class="pi pi-wallet"></i>
          Saldo residuo fatture
        </div>
        <div class="riepilogo-value">
          <span v-if="hasDebito">
            Debito: {{ currencyFormatter.format(resolvedSaldoResiduo) }}
          </span>
          <span v-else class="riepilogo-ok-text">Nessun debito</span>
        </div>
      </div>

      <div class="riepilogo-card" :class="hasPraticheAperte ? 'riepilogo-card--warning' : 'riepilogo-card--ok'">
        <div class="riepilogo-label">
          <i class="pi pi-briefcase"></i>
          Pratiche 
        </div>
        <div class="riepilogo-value">
          <span v-if="praticheLoading" class="riepilogo-muted">
            <i class="pi pi-spin pi-spinner"></i> Caricamento...
          </span>
          <span v-else-if="hasPraticheAperte">
            {{ praticheAperteCount }}
          </span>
          <span v-else class="riepilogo-ok-text">Nessuna pratica aperta</span>
        </div>
      </div>

      <!-- card in più della pagina che usa il riepilogo (es. lo stato dell'agente): diventano celle della griglia -->
      <slot name="cards" />
    </div>

    <div v-if="entityType === 'cliente'" class="riepilogo-forniture">
      <div class="riepilogo-label">
        <i class="pi pi-bolt"></i>
        {{ fornitureLabel }}
      </div>
      <div v-if="resolvedForniture.length" class="forniture-list">
        <template v-for="punto in resolvedForniture" :key="fornituraKey(punto)">
          <router-link
            v-if="punto.id"
            :to="fornituraPath(punto)"
            class="fornitura-chip"
            :title="punto.code || `${punto.typeLabel} #${punto.id}`"
          >
            <span class="fornitura-type">{{ punto.typeLabel }}</span>
            <span class="fornitura-code">{{ punto.code || `#${punto.id}` }}</span>
          </router-link>
          <span
            v-else
            class="fornitura-chip fornitura-chip--static"
            :title="punto.code"
          >
            <span class="fornitura-type">{{ punto.typeLabel }}</span>
            <span class="fornitura-code">{{ punto.code }}</span>
          </span>
        </template>
      </div>
      <span v-else class="riepilogo-muted">Nessuna fornitura</span>
    </div>
  </div>
</template>

<script>
import PodsService from '@/services/ask-management/pods-service'
import PdrsService from '@/services/ask-management/pdrs-service'

const COMPLETED_STATI = [
  'completata',
  'completato',
  'chiusa',
  'chiuso',
  'annullata',
  'annullato',
  'ko',
  'ok',
]

const EMPTY_PRATICHE = {
  pratiche: [],
  pratiche_gestione: [],
}

export default {
  name: 'SupplyRiepilogoSection',

  props: {
    contrattiVendita: {
      type: Array,
      default: () => [],
    },
    fatture: {
      type: Object,
      default: () => ({}),
    },
    praticheData: {
      type: Object,
      default: undefined,
    },
    clienteId: {
      type: [Number, String],
      default: null,
    },
    clienteNome: {
      type: String,
      default: null,
    },
    saldoResiduo: {
      type: Number,
      default: null,
    },
    supplyPoints: {
      type: Array,
      default: () => [],
    },
    entityType: {
      type: String,
      default: 'cliente',
      validator: (value) => ['cliente', 'rappresentante', 'agente'].includes(value),
    },
    fornitureLabel: {
      type: String,
      default: 'Forniture',
    },
  },

  data() {
    return {
      currencyFormatter: new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
      }),
      internalPraticheData: null,
      internalLoading: false,
      podsService: null,
      pdrsService: null,
    }
  },

  computed: {
    entityLabel() {
      if (this.entityType === 'agente') return 'Agente'
      return this.entityType === 'rappresentante' ? 'Rappresentante' : 'Cliente'
    },

    entityDetailPath() {
      if (!this.resolvedClienteId) return '#'
      if (this.entityType === 'agente') {
        return `/ask/agenti/${this.resolvedClienteId}/dettagli`
      }
      if (this.entityType === 'rappresentante') {
        return `/ask/rappresentanti/${this.resolvedClienteId}/dettagli`
      }
      return `/ask/clienti/${this.resolvedClienteId}/dettagli`
    },

    canOpenIncall() {
      return !!(this.resolvedClienteNome && String(this.resolvedClienteNome).trim())
    },

    contrattoCliente() {
      const contratti = Array.isArray(this.contrattiVendita) ? this.contrattiVendita : []
      if (!contratti.length) return null

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const attivi = contratti.filter((c) => {
        if (!c.data_fine) return true
        const fine = new Date(c.data_fine)
        return !isNaN(fine) && fine >= today
      })

      const pool = attivi.length ? attivi : contratti
      return pool.find((c) => c.cliente || c.cod_cliente) || pool[0] || null
    },

    resolvedClienteId() {
      if (this.clienteId !== null && this.clienteId !== undefined && this.clienteId !== '') {
        return this.clienteId
      }
      return this.contrattoCliente?.cod_cliente || null
    },

    resolvedClienteNome() {
      if (this.clienteNome) return this.clienteNome
      return this.contrattoCliente?.cliente || null
    },

    resolvedSaldoResiduo() {
      if (this.saldoResiduo !== null && this.saldoResiduo !== undefined && !Number.isNaN(this.saldoResiduo)) {
        return this.saldoResiduo > 0 ? this.saldoResiduo : 0
      }

      const fatture = this.fatture || {}
      if (fatture.saldo !== null && fatture.saldo !== undefined && !Number.isNaN(Number(fatture.saldo))) {
        const saldo = Number(fatture.saldo)
        return saldo > 0 ? saldo : 0
      }

      const totale = Number(fatture.importo_totale || 0)
      const pagato = Number(fatture.importo_pagato || 0)
      const residuo = totale - pagato
      return residuo > 0 ? residuo : 0
    },

    hasDebito() {
      return this.resolvedSaldoResiduo > 0
    },

    resolvedPraticheData() {
      if (this.praticheData !== undefined) {
        return this.praticheData
      }
      return this.internalPraticheData
    },

    praticheLoading() {
      if (this.praticheData !== undefined) {
        return this.praticheData === null
      }
      if (!this.supplyPoints.length) {
        return false
      }
      return this.internalLoading || this.internalPraticheData === null
    },

    praticheAperteCount() {
      const data = this.resolvedPraticheData
      if (!data) return 0

      const pratiche = Array.isArray(data.pratiche) ? data.pratiche : []
      const praticheGestione = Array.isArray(data.pratiche_gestione) ? data.pratiche_gestione : []

      const aperteFornitore = pratiche.filter((p) => this.isPraticaAperta(p)).length
      const aperteGestione = praticheGestione.filter((p) => this.isPraticaGestioneAperta(p)).length

      return aperteFornitore + aperteGestione
    },

    hasPraticheAperte() {
      return this.praticheAperteCount > 0
    },

    resolvedForniture() {
      const points = Array.isArray(this.supplyPoints) ? this.supplyPoints : []
      const seen = new Set()

      return points
        .map((point) => {
          if (!point) return null
          const type = point.type === 'pdr' ? 'pdr' : 'pod'
          const id = point.id || null
          const code = point.code || point.pod || point.pdr || null
          if (!id && !code) return null

          const key = id ? `${type}-${id}` : `${type}-code-${code}`
          if (seen.has(key)) return null
          seen.add(key)

          return {
            id,
            type,
            typeLabel: type === 'pdr' ? 'PDR' : 'POD',
            code,
          }
        })
        .filter(Boolean)
    },
  },

  watch: {
    supplyPoints: {
      deep: true,
      handler() {
        this.loadPraticheFromSupplyPoints()
      },
    },
  },

  mounted() {
    this.podsService = new PodsService(this.$http, this.$eventBus)
    this.pdrsService = new PdrsService(this.$http, this.$eventBus)
    this.loadPraticheFromSupplyPoints()
  },

  methods: {
    fornituraKey(punto) {
      return punto.id
        ? `${punto.type}-${punto.id}`
        : `${punto.type}-code-${punto.code}`
    },

    fornituraPath(punto) {
      if (!punto?.id) return '#'
      return punto.type === 'pdr'
        ? `/ask/pdrs/${punto.id}/dettagli`
        : `/ask/pods/${punto.id}/dettagli`
    },

    openInCall() {
      const query = (this.resolvedClienteNome || '').trim()
      if (!query) return

      const routeData = this.$router.resolve({
        path: '/in-call',
        query: {
          search_type: this.entityType,
          q: query,
        },
      })
      window.open(routeData.href, '_blank')
    },

    async loadPraticheFromSupplyPoints() {
      if (this.praticheData !== undefined) {
        return
      }

      const points = Array.isArray(this.supplyPoints)
        ? this.supplyPoints.filter((p) => p && p.id)
        : []

      if (!points.length) {
        this.internalPraticheData = { ...EMPTY_PRATICHE }
        this.internalLoading = false
        return
      }

      if (!this.podsService || !this.pdrsService) {
        return
      }

      this.internalLoading = true
      try {
        const results = await Promise.all(
          points.map(async (point) => {
            try {
              if (point.type === 'pdr') {
                const res = await this.pdrsService.getPraticheFornitori(point.id)
                return res?.item || EMPTY_PRATICHE
              }
              const res = await this.podsService.getPraticheFornitori(point.id)
              return res?.item || EMPTY_PRATICHE
            } catch (error) {
              console.error(error)
              return EMPTY_PRATICHE
            }
          })
        )

        this.internalPraticheData = {
          pratiche: results.flatMap((r) => r.pratiche || []),
          pratiche_gestione: results.flatMap((r) => r.pratiche_gestione || []),
        }
      } finally {
        this.internalLoading = false
      }
    },

    isCompletedStatus(value) {
      if (value === null || value === undefined || value === '') return false
      const normalized = String(value).trim().toLowerCase()
      return COMPLETED_STATI.some((status) => normalized.includes(status))
    },

    isPraticaAperta(pratica) {
      if (!pratica) return false
      if (pratica.data_esito) return false
      if (this.isCompletedStatus(pratica.esito) || this.isCompletedStatus(pratica.stato)) {
        return false
      }
      return true
    },

    isPraticaGestioneAperta(pratica) {
      if (!pratica) return false
      const stato = pratica.ultimo_stato || 'non lavorato'
      if (this.isCompletedStatus(stato)) return false
      return true
    },
  },
}
</script>

<style scoped>
.supply-riepilogo {
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
  background: #fff;
  border: 1px solid #dbe3ec;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.riepilogo-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1.1rem;
  background: linear-gradient(135deg, #0d3c61, #1a5490);
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
}

.riepilogo-header-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.riepilogo-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.riepilogo-incall-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 8px;
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.riepilogo-incall-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.22);
  border-color: #fff;
}

.riepilogo-incall-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.riepilogo-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  padding: 1rem;
}

.riepilogo-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 1.1rem;
}

.riepilogo-card--warning {
  background: #fff7ed;
  border-color: #fdba74;
}

.riepilogo-card--ok {
  background: #f0fdf4;
  border-color: #86efac;
}

.riepilogo-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.riepilogo-value {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 600;
  word-break: break-word;
}

.riepilogo-link {
  color: #0d3c61;
  text-decoration: none;
}

.riepilogo-link:hover {
  text-decoration: underline;
}

.riepilogo-muted {
  color: #94a3b8;
  font-weight: 500;
}

.riepilogo-ok-text {
  color: #15803d;
}

.riepilogo-forniture {
  border-top: 1px solid #e2e8f0;
  padding: 1rem 1.1rem 1.15rem;
  background: #f8fafc;
}

.riepilogo-forniture .riepilogo-label {
  margin-bottom: 0.65rem;
}

.forniture-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.fornitura-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
  padding: 0.35rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #0d3c61;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.fornitura-chip:hover {
  border-color: #0d3c61;
  background: #eff6ff;
}

.fornitura-chip--static {
  cursor: default;
}

.fornitura-chip--static:hover {
  border-color: #cbd5e1;
  background: #fff;
}

.fornitura-type {
  flex-shrink: 0;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.fornitura-code {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 960px) {
  .riepilogo-grid {
    grid-template-columns: 1fr;
  }
}
</style>
